import ArrowRight from "@/components/icons/ArrowRight";
import BlokWrap from "@/components/ui/BlokWrap";
import type { AnimationType } from "@/components/ui/BlokWrap/BlokWrap";
import { RichTextRender } from "@/components/ui/RichText";
import type { HeroCardStoryblok } from "@/generated/sb/sb-types";
import type { BlokProps } from "@frend-digital/storyblok";
import clsx from "clsx";
import { Asset, sizes } from "../Asset";
import { CompWrapper } from "./CompWrapper";
import styles from "./index.module.css";

const isValidLink = (link: HeroCardStoryblok["link"]) => {
  const newLink = link || {};
  return !!(
    link?.cached_url ||
    ("url" in newLink && newLink?.url) ||
    ("id" in newLink && newLink.id)
  );
};

export const HeroCardBlok = ({
  blok,
  preview,
  animation,
  pageOrder,
  smallHeading,
}: BlokProps<HeroCardStoryblok> & {
  animation?: AnimationType;
  pageOrder?: number;
  smallHeading?: boolean;
}) => {
  const { title, link, sticky } = blok;
  const getColorProps = () => {
    if ("titleColor" in blok) {
      const blokToUse = blok as typeof blok & {
        titleColor: {
          color: string;
        };
      };

      if (!blokToUse.titleColor.color) return {};

      return {
        style: {
          color: blokToUse.titleColor.color,
        },
      };
    }
    return {};
  };

  const validLink = isValidLink(link);

  const HeadingComponent = smallHeading ? "p" : "h1";

  return (
    <BlokWrap
      data-layout="page"
      editable={blok}
      animated={animation}
      className={clsx(styles.hero, "hero")}
    >
      <CompWrapper link={blok.link}>
        {blok.media && (
          <Asset
            src={blok.media?.[0]}
            sizes={sizes(100, 40)}
            audio={blok.media?.[0]?.audio}
            quality={90}
            priority={pageOrder === 1 ? true : undefined}
            autoPlay={true}
            loop={true}
            muted={true}
          />
        )}

        {title && (
          <div className={clsx(styles.content)}>
            <div
              className={clsx(styles.headerAndTitle, sticky && styles.sticky)}
            >
              {blok.heading && (
                <HeadingComponent id="hero-heading" className={styles.heading}>
                  {blok.heading}
                </HeadingComponent>
              )}
              <div
                style={{ ...getColorProps().style }}
                className={clsx(styles.title, validLink && styles.link)}
              >
                <h2>{title}</h2>
                {validLink && <ArrowRight />}
              </div>
            </div>
          </div>
        )}
      </CompWrapper>
      {blok.description && (
        <RichTextRender blok={blok.description} preview={preview} />
      )}
    </BlokWrap>
  );
};
